import { type BranchOption, type BranchPoint, type ResolvedBranchesData, type ResolvedBranchNode } from "@/lib/branchTypes"

export type InitialSelection = {
  selectedPath: string[]
  pendingScrollTargetId: string | null
}

export type SetSelectedPathOptions = {
  scrollTargetNodeId?: string | null
  updateUrl?: boolean
}

// Pre-measurement fallback for the HUD date. Keep this equal to the FIRST
// :::dateMarker in the content (currently 2026-01-01, opening the 2027
// chapter — same in the perspective docs), so the moment before the scroll
// measurement lands renders the same data the measurement will produce and
// the HUD's first frame can't flash a different date's state.
// Pre-measurement fallback for the HUD date. Keep this equal to the FIRST
// :::dateMarker in the content (currently 2027-01-01 — same in the
// perspective docs), so the moment before the scroll measurement lands
// renders the same data the measurement will produce and the HUD's first
// frame can't flash a different date's state.
export const INITIAL_GRAPH_DATE = new Date("2027-01-01T00:00:00Z")

const getChoiceIds = (choicesParam: string | null) => choicesParam?.split(",").filter(Boolean) || []

const appendValidChoiceId = (
  branchData: ResolvedBranchesData,
  validChoiceIds: string[],
  choiceId: string
) => {
  if (!branchData.nodes[choiceId]) return false
  validChoiceIds.push(choiceId)
  return true
}

const getValidChoiceIds = (
  branchData: ResolvedBranchesData,
  choiceIds: string[]
) => {
  const validChoiceIds: string[] = []
  for (const choiceId of choiceIds) {
    if (!appendValidChoiceId(branchData, validChoiceIds, choiceId)) break
  }
  return validChoiceIds
}

const getRootSelection = (branchData: ResolvedBranchesData): InitialSelection => ({
  selectedPath: [branchData.rootId],
  pendingScrollTargetId: null,
})

export const getInitialSelection = (
  branchData: ResolvedBranchesData,
  choicesParam: string | null
): InitialSelection => {
  const choiceIds = getChoiceIds(choicesParam)
  const validChoiceIds = getValidChoiceIds(branchData, choiceIds)
  if (validChoiceIds.length === 0) return getRootSelection(branchData)
  return {
    selectedPath: [branchData.rootId, ...validChoiceIds],
    pendingScrollTargetId: validChoiceIds[validChoiceIds.length - 1],
  }
}

const visitChildNode = (
  branchData: ResolvedBranchesData,
  parentNodeId: string,
  targetId: string | null,
  parentMap: Record<string, string>,
  visited: Set<string>
) => {
  if (!targetId || targetId === parentNodeId) return
  if (!parentMap[targetId]) parentMap[targetId] = parentNodeId
  visitParentMapNode(branchData, targetId, parentMap, visited)
}

const visitBranchOptions = (
  branchData: ResolvedBranchesData,
  parentNodeId: string,
  options: BranchOption[],
  parentMap: Record<string, string>,
  visited: Set<string>
) => {
  for (const option of options) {
    visitChildNode(branchData, parentNodeId, option.targetId, parentMap, visited)
  }
}

const visitBranchPoints = (
  branchData: ResolvedBranchesData,
  nodeId: string,
  branchPoints: BranchPoint[],
  parentMap: Record<string, string>,
  visited: Set<string>
) => {
  for (const branchPoint of branchPoints) {
    visitBranchOptions(branchData, nodeId, branchPoint.options, parentMap, visited)
  }
}

const visitParentMapNode = (
  branchData: ResolvedBranchesData,
  nodeId: string,
  parentMap: Record<string, string>,
  visited: Set<string>
) => {
  if (visited.has(nodeId)) return
  visited.add(nodeId)
  const branchPoints = branchData.nodes[nodeId]?.branchPoints || []
  visitBranchPoints(branchData, nodeId, branchPoints, parentMap, visited)
}

export const buildParentMap = (branchData: ResolvedBranchesData) => {
  const parentMap: Record<string, string> = {}
  visitParentMapNode(branchData, branchData.rootId, parentMap, new Set<string>())
  return parentMap
}

const appendNodeLineage = (
  nodeId: string | undefined,
  parentMap: Record<string, string>,
  visited: Set<string>,
  path: string[]
) => {
  if (!nodeId || visited.has(nodeId)) return
  visited.add(nodeId)
  appendNodeLineage(parentMap[nodeId], parentMap, visited, path)
  path.push(nodeId)
}

export const buildPathToNode = (
  nodeId: string,
  parentMap: Record<string, string>
) => {
  const path: string[] = []
  appendNodeLineage(nodeId, parentMap, new Set<string>(), path)
  return path
}

// Walks the primary `targetId` chain from a start node. Returns [startNodeId, ...primaryDescendants].
// Stops at the first node that has no primary option or revisits an already-seen node.
export const buildPrimaryChain = (
  startNodeId: string,
  nodes: Record<string, ResolvedBranchNode>
): string[] => {
  if (!nodes[startNodeId]) return []
  const chain: string[] = [startNodeId]
  const visited = new Set<string>(chain)
  let currentNodeId = startNodeId
  while (true) {
    const branchOptions = nodes[currentNodeId]?.branchPoints?.[0]?.options ?? []
    const primaryOption = branchOptions.find(
      option => option.targetId && (option.primary || nodes[option.targetId]?.primary)
    )
    const nextNodeId = primaryOption?.targetId
    if (!nextNodeId || visited.has(nextNodeId)) break
    visited.add(nextNodeId)
    chain.push(nextNodeId)
    currentNodeId = nextNodeId
  }
  return chain
}

const updateChoicesParam = (url: URL, choices: string[]) => {
  if (choices.length > 0) {
    url.searchParams.set("choices", choices.join(","))
    return
  }
  url.searchParams.delete("choices")
}

export const syncSelectedPathToUrl = (path: string[]) => {
  const url = new URL(window.location.href)
  updateChoicesParam(url, path.slice(1))
  window.history.replaceState({}, "", url.toString())
}

export const mergeVisitedNodeIds = (previousNodeIds: string[], path: string[]) => {
  const next = new Set(previousNodeIds)
  for (const nodeId of path) {
    next.add(nodeId)
  }
  return Array.from(next)
}

const hasNewlyRevealedNodeId = (
  previousNodeIds: string[],
  nextNodeIds: string[]
) => {
  const previousNodeIdSet = new Set(previousNodeIds)
  return nextNodeIds.some(nodeId => !previousNodeIdSet.has(nodeId))
}

export const mergeRevealedNodeIds = (
  previousNodeIds: string[],
  nextNodeIds: string[]
) => {
  if (!hasNewlyRevealedNodeId(previousNodeIds, nextNodeIds)) return previousNodeIds
  return [...new Set([...previousNodeIds, ...nextNodeIds])]
}

export const clampProgress = (value: number) => Math.max(0, Math.min(1, value))

export const getElementScrollProgress = (
  element: Element,
  viewportCenter: number
) => {
  const rect = element.getBoundingClientRect()
  const raw = rect.height > 0 ? (viewportCenter - rect.top) / rect.height : 0
  return clampProgress(raw)
}

export const getCenteredScrollTop = (element: HTMLElement) => {
  const elementTop = element.getBoundingClientRect().top + window.scrollY
  return Math.max(0, elementTop - window.innerHeight * 0.5)
}

export const getTopAlignedScrollTop = (element: HTMLElement) => {
  const elementTop = element.getBoundingClientRect().top + window.scrollY
  return Math.max(0, elementTop - window.innerHeight * 0.05)
}

// Scroll an element into view on a page that is still progressively hydrating.
//
// Both deep-link and selection navigation hit the same hazard: the target
// element appears in the DOM long before the markdown/images ABOVE it finish
// laying out, so its resting scroll position keeps drifting for many frames.
// A one-shot scroll (even a "settle, then smooth" one) computes a target, fires
// once, and then stops correcting — so any reflow that lands during the glide
// leaves the reader short of the target (the classic "only scrolled one page"
// bug). The robust strategy is to RE-AIM every frame with behavior:"auto" until
// the computed target holds steady, so we always converge on the final layout.
//
// `align` picks where the element lands (top-aligned vs centered). `beforeScroll`
// runs once when the element is found — used to force a collapsed <details> open
// so its content is laid out before we measure.
type StabilizeScrollOptions = {
  align?: (element: HTMLElement) => number
  beforeScroll?: (element: HTMLElement) => void
}

const FIND_MAX_FRAMES = 240 // ~4s at 60fps
const STABILIZE_MAX_FRAMES = 180 // ~3s at 60fps
const REQUIRED_STABLE_FRAMES = 8

export const stabilizeScrollToElementId = (
  elementId: string,
  // A monotonically-increasing request token; bump it to cancel an in-flight
  // scroll when a newer navigation arrives. The loop bails the moment
  // `requestIdRef.current` no longer equals the token captured at start.
  requestIdRef: { current: number },
  options: StabilizeScrollOptions = {},
  // Called once the scroll settles. `found` is false when the element never
  // appeared within the find budget, so callers can run a fallback.
  onDone?: (found: boolean) => void,
) => {
  if (typeof window === "undefined") return
  const align = options.align ?? getTopAlignedScrollTop
  const requestId = ++requestIdRef.current

  const stabilize = (element: HTMLElement) => {
    let lastTop = Number.NaN
    let stableFrames = 0
    let frames = 0
    const tick = () => {
      if (requestIdRef.current !== requestId) return
      const target = align(element)
      if (Math.abs(window.scrollY - target) > 1) {
        window.scrollTo({ top: target, behavior: "auto" })
      }
      stableFrames = Math.abs(target - lastTop) < 1 ? stableFrames + 1 : 0
      lastTop = target
      if (stableFrames >= REQUIRED_STABLE_FRAMES || ++frames >= STABILIZE_MAX_FRAMES) {
        return onDone?.(true)
      }
      requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
  }

  const findElement = (attempts: number) => {
    if (requestIdRef.current !== requestId) return
    const element = document.getElementById(elementId)
    if (element) {
      options.beforeScroll?.(element)
      stabilize(element)
      return
    }
    if (attempts < FIND_MAX_FRAMES) {
      requestAnimationFrame(() => findElement(attempts + 1))
      return
    }
    onDone?.(false)
  }
  requestAnimationFrame(() => findElement(0))
}
